import { Grid, Box, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import Accordion from '../../../components/Accordion';
import { useFlutter } from '../../../components/FlutterProvider';
import { useLoadingController } from '../../../components/LoadingProvider';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';
import OutlinedSelectField from '../../../components/fields/OutlinedSelectField';
import Switch from '../../../components/fields/Switch';
import useCommonStyles from '../../../useCommonStyles';
import { useMessageTemplateOptionsQuery } from '../../../utilities/useMessageTemplateOptions';

const SettingsBody = () => {
    const { t } = useTranslation(['settingsPage']);
    const { capabilities, deviceFingerPrint } = useFlutter();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const commonStyles = useCommonStyles();
    const user = useCurrentUser();
    const { options: templatesOptions, loading: messageTemplatesLoading } = useMessageTemplateOptionsQuery();
    useLoadingController(messageTemplatesLoading);

    // As dicussion with PM, the PTI has no mobile support yet
    // we can hide biometric and push notification settings if the device doesn't support it.
    // This will avoid confusion for users
    const isDeviceCompatibleWithBiometric = capabilities.hasFingerPrint || capabilities.hasFaceID;
    const outlinedSelectInputProps = {
        classes: {
            icon: commonStyles.fillSecondary,
        },
    };
    const biddingTemplateFields = [
        {
            label: t('settingsPage:label.messageTemplateUseCases.upcomingBid'),
            name: 'upcomingBidTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidOpen'),
            name: 'biddingOpenedTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidSubmission'),
            name: 'bidSubmissionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.highestBidSubmission'),
            name: 'highestBidSubmissionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.noChancesLeft'),
            name: 'noChancesLeftTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidAwarded'),
            name: 'bidAwardedTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidAwardedForSalesConsultant'),
            name: 'bidAwardedForSalesConsultantTemplateUseCase',
        },
    ];
    const userAccountTemplateFields = [
        {
            label: t('settingsPage:label.messageTemplateUseCases.otp'),
            name: 'otpTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.userAccountRejection'),
            name: 'userAccountRejectionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.userAccountApproval'),
            name: 'userAccountApprovalTemplateUseCase',
        },
    ];

    const renderMessageTemplateFields = (fields: Array<{ label: string; name: string }>) =>
        fields.map(field => (
            <Grid key={field.name} xs={12} item>
                <OutlinedSelectField
                    color="secondary"
                    inputProps={outlinedSelectInputProps}
                    label={field.label}
                    name={field.name}
                    options={templatesOptions}
                />
            </Grid>
        ));

    return (
        <Box ml={isSmall ? 2 : 0} mr={isSmall ? 2 : 0} mt={isSmall ? 12 : 0}>
            <Grid spacing={2} container>
                <Grid hidden={!isDeviceCompatibleWithBiometric} lg={6} md={6} sm={12} xs={12} item>
                    <Accordion title={t('settingsPage:accordionTitle.login')} defaultExpanded>
                        <Grid spacing={2} container>
                            <Grid xs={12} item>
                                <Switch
                                    label={t('settingsPage:label.biometric')}
                                    labelPlacement="start"
                                    name="biometric"
                                />
                            </Grid>
                        </Grid>
                    </Accordion>
                </Grid>
                <Grid hidden={!isDeviceCompatibleWithBiometric} lg={6} md={6} sm={12} xs={12} item>
                    <Accordion title={t('settingsPage:accordionTitle.notifications')} defaultExpanded>
                        <Grid spacing={2} container>
                            <Grid xs={12} item>
                                <Switch
                                    disabled={!deviceFingerPrint}
                                    label={t('settingsPage:label.pushNotification')}
                                    labelPlacement="start"
                                    name="pushNotification"
                                />
                            </Grid>
                        </Grid>
                    </Accordion>
                </Grid>
                {user.type === 'Admin' && (
                    <>
                        <Grid lg={6} md={6} sm={12} xs={12} item>
                            <Accordion
                                title={t('settingsPage:accordionTitle.selectMessageTemplatesForBidding')}
                                defaultExpanded
                            >
                                <Grid spacing={2} container>
                                    {renderMessageTemplateFields(biddingTemplateFields)}
                                </Grid>
                            </Accordion>
                        </Grid>
                        <Grid lg={6} md={6} sm={12} xs={12} item>
                            <Accordion
                                title={t('settingsPage:accordionTitle.selectMessageTemplatesForUserAccount')}
                                defaultExpanded
                            >
                                <Grid spacing={2} container>
                                    {renderMessageTemplateFields(userAccountTemplateFields)}
                                </Grid>
                            </Accordion>
                        </Grid>
                        <Grid lg={6} md={6} sm={12} xs={12} item>
                            <Accordion title={t('settingsPage:accordionTitle.system')} defaultExpanded>
                                <Grid spacing={2} container>
                                    <Grid xs={12} item>
                                        <OutlinedInputField
                                            color="secondary"
                                            label={t('settingsPage:label.contactEmail')}
                                            name="systemContactEmail"
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            </Accordion>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default SettingsBody;
